// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback } from 'react';

import type { ReviewStatus, SurveyType } from '../../../api/types';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { neighbourAfter } from '../../../bygning/review';
import type { Selection } from '../../../bygning/selection';

/**
 * The inspector's Godkend / Fejl / back-to-queue buttons, with the shell's
 * G/A rules: never stacked while a write is queued, and when the type leaves
 * the mode's strip (`inStrip`) the selection moves to its neighbour there.
 * A type that was not in the strip (Ressourcer under a room focus: a type
 * outside the room) keeps the selection. A refused approval (422: samples
 * open) is toasted by `actions.review`.
 */
export function useReview(
  data: CaseData,
  actions: CaseActions,
  setSel: (patch: Partial<Selection>) => void,
  inStrip: (t: SurveyType) => boolean,
) {
  return useCallback(
    (type: SurveyType, status: ReviewStatus) => {
      if (data.busy || type.review_status === status) return;
      const wasIn = inStrip(type);
      const list = data.types.filter(inStrip);
      const next = neighbourAfter(list, type.id);
      void actions.review(type.id, status).then((body) => {
        if (body && wasIn && !inStrip(body)) setSel({ typeId: next });
      });
    },
    [data.busy, data.types, actions, setSel, inStrip],
  );
}
